<template>
    <div>
        <FullPageLoader :isLoading="loading"/>

        <!-- Delete Confirmation Modal -->
        <b-modal v-model="showDeleteModal" :width="640" scroll="keep">
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">Korting verwijderen</p>
                </header>
                <section class="modal-card-body" style="overflow: hidden;border-radius:0;">
                    <p>Weet je zeker dat je <strong>{{ form.name }}</strong> wilt verwijderen?</p>
                    <b-message v-if="discount && discount.contract_product_discounts_count > 0" type="is-warning">
                        <strong>Let op:</strong> Deze korting wordt gebruikt in {{ discount.contract_product_discounts_count }} contract(en).
                        Deze worden niet automatisch verwijderd.
                    </b-message>
                </section>
                <footer class="modal-card-foot text-right">
                    <b-button @click="showDeleteModal = false">Annuleren</b-button>
                    <b-button type="is-danger" :loading="deleting" @click="deleteDiscount">Verwijderen</b-button>
                </footer>
            </div>
        </b-modal>

        <div class="row">
            <div class="column medium-3 text-right float-right">
                <b-button tag="router-link" :to="{ name: 'DiscountsOverview' }" type="is-dark">Terug naar overzicht</b-button>
            </div>
            <div class="column medium-9">
                <h1>{{ isNew ? 'Korting toevoegen' : 'Korting bewerken' }}</h1>
            </div>
        </div>

        <div class="row">
            <div class="column">
                <form class="form" @submit.prevent="saveDiscount">
                    <b-tabs size="is-medium" class="block" v-model="tabIndex">
                        <b-tab-item label="Algemeen">
                            <b-field
                                label="Naam"
                                :message="getError(errors, 'name')"
                                :type="getType(errors, 'name', '')"
                            >
                                <b-input
                                    v-model="form.name"
                                    placeholder="Bijv. Nieuwe klant korting"
                                    required
                                ></b-input>
                            </b-field>

                            <b-field
                                label="Omschrijving"
                                :message="getError(errors, 'description')"
                                :type="getType(errors, 'description', '')"
                            >
                                <b-input
                                    v-model="form.description"
                                    type="textarea"
                                    rows="3"
                                    placeholder="Omschrijving van de korting (optioneel)"
                                ></b-input>
                            </b-field>

                            <b-field
                                label="Status"
                                :message="getError(errors, 'active')"
                                :type="getType(errors, 'active', '')"
                            >
                                <b-checkbox v-model="form.active">
                                    Actief (beschikbaar voor selectie)
                                </b-checkbox>
                            </b-field>
                        </b-tab-item>

                        <b-tab-item label="Korting(en)">
                            <div class="row">
                                <div class="column is-9">
                                    <strong>Voorbeeld productprijs</strong>
                                    <span class="help">Pas dit aan om te zien hoe de korting werkt bij verschillende prijzen.</span>
                                </div>
                                <div class="column is-3">
                                    <b-field>
                                        <b-input
                                            v-model.number="examplePrice"
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            placeholder="100"
                                        >
                                            <template #prepend>
                                                <b-icon icon="euro-sign"></b-icon>
                                            </template>
                                        </b-input>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <hr>
                                </div>
                            </div>

                            <invoice-discount-manager
                                :discounts.sync="form.discounts"
                                :apply-to-all.sync="applyToAllInvoices"
                                :product-price="examplePrice"
                                :disabled="false"
                                :show-description="false"
                                :show-help-text="false"
                            />

                            <hr>

                            <h4>Voorbeeld (op basis van €{{ examplePrice.toFixed(2).replace('.', ',') }} productprijs)</h4>
                            <table class="table is-fullwidth is-striped">
                                <thead>
                                    <tr>
                                        <th>Factuur</th>
                                        <th>Korting</th>
                                        <th>Kortingsbedrag</th>
                                        <th>Oude prijs</th>
                                        <th>Nieuwe prijs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(discount, index) in form.discounts" :key="index">
                                        <td><strong>{{ getInvoiceLabel(index) }}</strong></td>
                                        <td class="has-text-danger">{{ getDiscountPreview(discount) }}</td>
                                        <td class="has-text-danger">-€{{ calculateDiscountAmount(discount) }}</td>
                                        <td class="has-text-weight-bold">€{{ (examplePrice || 0).toFixed(2).replace('.', ',') }}</td>
                                        <td class="has-text-weight-bold">€{{ calculateNewPrice(discount) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </b-tab-item>

                        <b-tab-item label="Gebruik" v-if="!isNew && discount">
                            <div v-if="loadingUsage" class="has-text-centered" style="padding: 40px;">
                                <b-loading :active="true" :is-full-page="false"></b-loading>
                            </div>

                            <div v-else-if="usageData.length === 0" class="has-text-centered" style="padding: 40px;">
                                <p class="has-text-grey margin0">Deze korting is nog niet gebruikt.</p>
                            </div>

                            <b-table
                                v-else
                                :data="usageData"
                                :paginated="true"
                                :per-page="usagePerPage"
                                :total="usageTotal"
                                :current-page="usageCurrentPage"
                                @page-change="onUsagePageChange"
                                backend-pagination
                                :loading="loadingUsage"
                                striped
                                hoverable
                            >
                                <b-table-column field="invoice_number" label="Factuurnummer" v-slot="props">
                                    <strong>{{ props.row.invoice_number }}</strong>
                                </b-table-column>

                                <b-table-column field="invoice_date" label="Factuurdatum" v-slot="props">
                                    {{ formatDate(props.row.invoice_date) }}
                                </b-table-column>

                                <b-table-column field="product_name" label="Product" v-slot="props">
                                    {{ props.row.product_name }}
                                </b-table-column>

                                <b-table-column field="original_price" label="Originele prijs" v-slot="props">
                                    €{{ props.row.original_price }}
                                </b-table-column>

                                <b-table-column field="discounted_price" label="Korting prijs" v-slot="props">
                                    <span class="has-text-success">€{{ props.row.discounted_price }}</span>
                                </b-table-column>

                                <b-table-column field="discount_amount" label="Kortingsbedrag" v-slot="props">
                                    <span class="has-text-danger">-€{{ props.row.discount_amount }}</span>
                                </b-table-column>

                                <b-table-column field="actions" label="" v-slot="props" numeric>
                                    <b-button
                                        type="is-dark"
                                        size="is-small"
                                        tag="router-link"
                                        :to="{ name: 'InvoicesRecord', params: { id: props.row.invoice_id } }"
                                    >
                                        Bekijken
                                    </b-button>
                                </b-table-column>
                            </b-table>
                        </b-tab-item>
                    </b-tabs>
                </form>

                <div class="row">
                    <div class="column">
                        <div class="formfooter text-right">
                            <b-button
                                v-if="!isNew"
                                type="is-danger"
                                outlined
                                :loading="deleting"
                                @click="showDeleteModal = true"
                            >
                                Verwijderen
                            </b-button>
                            <b-button
                                type="is-success"
                                native-type="submit"
                                :loading="saving"
                                :disabled="saving"
                                @click="saveDiscount"
                            >
                                Opslaan en sluiten
                            </b-button>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PageTitle from '../PageTitle.vue';
import FullPageLoader from '../FullPageLoader.vue';
import InvoiceDiscountManager from './components/InvoiceDiscountManager.vue';
import formvalidationMixin from '@/mixins/formvalidationMixin.js'
import axios from 'axios';

export default {
    name: 'DiscountsRecord',

    components: {
        PageTitle,
        FullPageLoader,
        InvoiceDiscountManager
    },

    mixins: [formvalidationMixin],

    data() {
        return {
            tabIndex: 0,
            discount: null,
            form: {
                name: '',
                description: '',
                discounts: [
                    {
                        type: 'percentage',
                        value: 0,
                        invoice_number: null // null means applies to all
                    }
                ],
                active: true
            },
            applyToAllInvoices: true,
            examplePrice: 100, // Default example price
            errors: {},
            saving: false,
            loading: false,
            deleting: false,
            showDeleteModal: false,
            // Usage tab data
            usageData: [],
            usageTotal: 0,
            usagePerPage: 10,
            usageCurrentPage: 1,
            loadingUsage: false
        };
    },

    computed: {
        isNew() {
            return this.$route.params.id === 'nieuw';
        }
    },

    mounted() {
        if (!this.isNew) {
            this.fetchDiscount();
        }
    },

    methods: {
        async fetchDiscount() {
            this.loading = true;
            try {
                const headers = { 'Authorization': 'bearer ' + this.$store.state.authToken };
                const response = await axios.get(`/api/discounts/${this.$route.params.id}`, { headers });
                this.discount = response.data;

                // Populate form
                this.form.name = this.discount.name;
                this.form.description = this.discount.description || '';
                this.form.active = this.discount.active;

                // Handle discounts
                if (this.discount.discounts && this.discount.discounts.length > 0) {
                    this.form.discounts = this.discount.discounts.map((discount, index) => ({
                        type: discount.type,
                        value: parseFloat(discount.value),
                        invoice_number: this.discount.discounts.length === 1 ? null : (index + 1)
                    }));

                    // Set applyToAllInvoices based on existing discount
                    if (this.discount.discounts.length === 1) {
                        this.applyToAllInvoices = this.discount.discounts[0].invoice_number === null;
                    }
                }
            } catch (error) {
                this.$buefy.toast.open({
                    message: 'Fout bij ophalen korting',
                    type: 'is-danger'
                });
                console.error(error);
            } finally {
                this.loading = false;
            }
        },

        async saveDiscount() {
            this.saving = true;
            this.errors = {};

            try {
                // Validate that all discounts have valid values (>= 0)
                const hasInvalidValues = this.form.discounts.some(discount =>
                    discount.value === null ||
                    discount.value === undefined ||
                    discount.value < 0
                );
                if (hasInvalidValues) {
                    this.$buefy.toast.open({
                        message: 'Alle kortingen moeten een waarde van 0 of hoger hebben',
                        type: 'is-warning'
                    });
                    this.saving = false;
                    return;
                }

                // Prepare data (0% discounts are now valid)
                const data = {
                    name: this.form.name,
                    description: this.form.description,
                    active: this.form.active,
                    discounts: this.form.discounts.map((discount, index) => ({
                        type: discount.type,
                        value: discount.value,
                        invoice_number: this.form.discounts.length === 1 && this.applyToAllInvoices ? null : (index + 1)
                    }))
                };

                let response;
                const headers = { 'Authorization': 'bearer ' + this.$store.state.authToken };

                if (this.isNew) {
                    response = await axios.post('/api/discounts', data, { headers });
                } else {
                    response = await axios.put(`/api/discounts/${this.$route.params.id}`, data, { headers });
                }

                this.$buefy.toast.open({
                    message: response.data.success || 'Korting succesvol opgeslagen',
                    type: 'is-success'
                });

                this.$router.push({ name: 'DiscountsOverview' });
            } catch (error) {
                if (error.response && error.response.data.errors) {
                    this.errors = error.response.data.errors;
                }
                this.$buefy.toast.open({
                    message: 'Fout bij opslaan korting',
                    type: 'is-danger'
                });
                console.error(error);
            } finally {
                this.saving = false;
            }
        },

        addInvoiceDiscount() {
            this.form.discounts.push({
                type: 'percentage',
                value: 0,
                invoice_number: this.form.discounts.length + 1
            });
        },

        removeInvoiceDiscount(index) {
            if (this.form.discounts.length > 1) {
                this.form.discounts.splice(index, 1);
            }
        },

        getInvoiceLabel(index) {
            if (this.form.discounts.length === 1) {
                return 'Alle facturen';
            }
            if (index === 0) return '1e factuur';
            if (index === 1) return '2e factuur';
            if (index === 2) return '3e factuur';
            return `${index + 1}e factuur`;
        },

        getNextInvoiceLabel() {
            const nextIndex = this.form.discounts.length;
            if (nextIndex === 0) return '1e factuur';
            if (nextIndex === 1) return '2e factuur';
            if (nextIndex === 2) return '3e factuur';
            return `${nextIndex + 1}e factuur`;
        },

        getDiscountPreview(discount) {
            if (discount.type === 'percentage') {
                return `${discount.value}%`;
            }
            return `€${discount.value.toFixed(2).replace('.', ',')}`;
        },

        calculateDiscountAmount(discount) {
            let amount;
            if (discount.type === 'percentage') {
                amount = this.examplePrice * (discount.value / 100);
            } else {
                amount = Math.min(discount.value, this.examplePrice);
            }
            return amount.toFixed(2).replace('.', ',');
        },

        calculateNewPrice(discount) {
            let discountAmount;
            if (discount.type === 'percentage') {
                discountAmount = this.examplePrice * (discount.value / 100);
            } else {
                discountAmount = Math.min(discount.value, this.examplePrice);
            }
            const newPrice = Math.max(0, this.examplePrice - discountAmount);
            return newPrice.toFixed(2).replace('.', ',');
        },

        formatDate(date) {
            return this.moment(date).format('DD-MM-YYYY');
        },

        async deleteDiscount() {
            this.deleting = true;

            try {
                const headers = { 'Authorization': 'bearer ' + this.$store.state.authToken };
                const response = await axios.delete(`/api/discounts/${this.$route.params.id}`, { headers });

                if (response.data.success) {
                    this.$buefy.toast.open({
                        message: response.data.success,
                        type: 'is-success'
                    });
                }

                this.$router.push({ name: 'DiscountsOverview' });
            } catch (error) {
                this.$buefy.toast.open({
                    message: error.response?.data?.error || 'Fout bij verwijderen korting',
                    type: 'is-danger'
                });
                console.error(error);
            } finally {
                this.deleting = false;
                this.showDeleteModal = false;
            }
        },

        async fetchUsageData(page = 1) {
            this.loadingUsage = true;
            try {
                const headers = { 'Authorization': 'bearer ' + this.$store.state.authToken };
                const response = await axios.get(`/api/discounts/${this.$route.params.id}/usage`, {
                    headers,
                    params: {
                        page: page,
                        per_page: this.usagePerPage
                    }
                });

                this.usageData = response.data.data;
                this.usageTotal = response.data.total;
                this.usageCurrentPage = response.data.current_page;
            } catch (error) {
                this.$buefy.toast.open({
                    message: 'Fout bij ophalen gebruiksgegevens',
                    type: 'is-danger'
                });
                console.error(error);
            } finally {
                this.loadingUsage = false;
            }
        },

        onUsagePageChange(page) {
            this.fetchUsageData(page);
        }
    },

    watch: {
        tabIndex: {
            handler(newVal) {
                if (newVal === 2) {
                    this.fetchUsageData();
                }
            }
        }
    }
}
</script>
